import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '@/context/CartContext';

export default function Cart() {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-24 md:py-32 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <ShoppingBag className="w-24 h-24 text-muted mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4 font-heading" data-testid="empty-cart-title">Your Cart is Empty</h2>
          <p className="text-muted-foreground mb-8">Add some products to get started!</p>
          <button
            onClick={() => navigate('/products')}
            className="bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 inline-flex items-center gap-2"
            data-testid="continue-shopping-button"
          >
            Continue Shopping
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-4 font-heading" data-testid="cart-title">Shopping Cart</h1>
          <p className="text-muted-foreground mb-12">{getCartCount()} items in your cart</p>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-soft"
                  data-testid={`cart-item-${index}`}
                >
                  <div className="flex gap-6">
                    <div className="w-32 h-32 flex-shrink-0 bg-gradient-to-br from-primary/10 to-white rounded-xl overflow-hidden">
                      <img loading="lazy" decoding="async"
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex justify-between mb-2">
                        <h3 className="text-xl font-semibold font-heading" data-testid={`cart-item-name-${index}`}>
                          {item.name}
                        </h3>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-muted-foreground hover:text-red-500 transition-colors"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      <p className="text-muted-foreground text-sm mb-2">{item.description}</p>
                      {item.is_subscription && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-primary/10 text-primary text-xs px-3 py-1 rounded-full font-semibold">
                            Subscription: First {item.subscription_months} month FREE
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center border-2 border-border rounded-xl overflow-hidden">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="px-3 py-2 hover:bg-primary/10 transition-colors"
                            data-testid={`decrease-quantity-${index}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 py-2 font-semibold" data-testid={`item-quantity-${index}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="px-3 py-2 hover:bg-primary/10 transition-colors"
                            data-testid={`increase-quantity-${index}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            £{item.price.toFixed(2)} each
                            {item.is_subscription && <span className="text-primary font-semibold ml-1">(1st month FREE)</span>}
                          </p>
                          <p className="text-2xl font-bold text-primary" data-testid={`item-total-${index}`}>
                            £{(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl p-8 shadow-soft sticky top-24"
              >
                <h2 className="text-2xl font-bold mb-6 font-heading">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-semibold">£{getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="font-semibold text-primary">Free</span>
                  </div>
                  <div className="border-t border-border pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total</span>
                      <span className="text-3xl font-bold text-primary" data-testid="cart-total">
                        £{getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    // Only BCM-95® is currently purchasable. Aggregate all
                    // BCM-95® items in cart into a single checkout payload.
                    // (When Rhuleave-K launches, we'll switch to a true
                    // multi-line checkout endpoint.)
                    const purchasable = cart.find(
                      (item) => item.available_for_purchase !== false,
                    ) || cart[0];
                    const totalQty = cart
                      .filter((it) => it.id === purchasable.id)
                      .reduce((s, it) => s + (it.quantity || 1), 0);
                    navigate('/checkout', {
                      state: {
                        productId: purchasable.id,
                        quantity: totalQty,
                        product: purchasable,
                      },
                    });
                  }}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl mb-4"
                  data-testid="proceed-checkout-button"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-5 h-5" />
                </button>

                <button
                  onClick={() => navigate('/products')}
                  className="w-full bg-white text-secondary border-2 border-border hover:border-primary/30 font-medium py-4 px-6 rounded-xl transition-all duration-300"
                  data-testid="continue-shopping-cart-button"
                >
                  Continue Shopping
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}